/**
 * Shared labels for pricing insight UIs (admin product panel, seller panel, Insights).
 * Keeps AI vs statistical messaging consistent across surfaces.
 */

import {
  getCombinedCohortProvenanceLabel,
  getPricingDataSourceBadgeLabel,
  normalizePricingDataSource,
  type PricingDataSource,
  type PricingDataSourceInput,
} from "@/lib/pricing/pricingDataSource";
import type { CombinedPricingProvenance } from "@/lib/pricing/combinedCohortBranchSelection";
import type { TitleCohortMatchMode } from "@/lib/pricingTitleCohort";
import type { AdminPriceSuitabilityStatus } from "@/lib/server/adminPriceSuitability";
import type { SellPriceBandPolicy } from "@/types/price-insight";
import type { BandPriceChipTier } from "@/lib/pricing/sellerPriceBandRecommendations";

export type PricingInsightModeUi = "admin" | "user";

export type PricingAiProviderUi = "genkit" | "openai" | "none" | undefined;

/** True when platform/admin path will call Genkit or OpenAI (not statistical-only). */
export function usesAiAssistedPricing(
  aiProvider: PricingAiProviderUi,
): boolean {
  return aiProvider === "genkit" || aiProvider === "openai";
}

export function getAnalysisHeading(aiProvider: PricingAiProviderUi): string {
  if (usesAiAssistedPricing(aiProvider)) {
    return "AI-assisted analysis";
  }
  return "Market data analysis";
}

function getPricingStatsSourcePhrase(
  dataSource: PricingDataSourceInput | undefined,
): string {
  const canonical = normalizePricingDataSource(dataSource ?? "combined");
  if (canonical === "market_reference") {
    return "synced market reference rows";
  }
  if (canonical === "approved_listings") {
    return "approved Sell It Off listings";
  }
  return "approved listings and synced market reference";
}

export function getAnalysisSubtitle(
  aiProvider: PricingAiProviderUi,
  mode: PricingInsightModeUi,
  dataSource?: PricingDataSourceInput,
): string {
  const statsSource = getPricingStatsSourcePhrase(dataSource);
  const settingsRef =
    mode === "admin"
      ? "Uses the same defaults as Admin → Settings."
      : "Based on platform market data.";
  if (usesAiAssistedPricing(aiProvider)) {
    return `AI-assisted suggestion from ${statsSource}. ${settingsRef}`;
  }
  return `Market-based suggestion from ${statsSource}. ${settingsRef}`;
}

/** AI Insights tab — note when pricing stats use market reference but tables show approved listings. */
export function getAiInsightsPricingDataNote(
  dataSource: PricingDataSourceInput,
): string {
  const canonical = normalizePricingDataSource(dataSource);
  if (canonical === "market_reference") {
    return "Suggested prices use synced market reference rows in the database (including title-based cohorts when enough rows match). The listing tables below show approved Sell It Off items for context only.";
  }
  if (canonical === "approved_listings") {
    return "Suggested prices and listing tables both use approved Sell It Off listings.";
  }
  return "Suggested prices merge synced market reference rows with approved listings. The listing tables below show approved Sell It Off items for context only.";
}

export function getAiInsightsNoTitleCohortNote(
  dataSource: PricingDataSourceInput,
): string {
  const canonical = normalizePricingDataSource(dataSource);
  const statsPart =
    canonical === "market_reference"
      ? "synced market reference rows"
      : canonical === "approved_listings"
        ? "approved Sell It Off listings"
        : "synced market reference and approved listings";
  return `Pricing uses the full category/type segment from ${statsPart}; use the "Rest of segment" tab to browse approved marketplace listings.`;
}

/** Admin/seller note when stats use a widened capacity bucket (Phase 5). */
export function getFuzzyTitleCohortBandNote(
  signalSummary: string,
  toleranceLabel: string,
): string {
  return `Market band uses nearby capacities (${toleranceLabel}) because exact ${signalSummary} title matches were limited.`;
}

export function getAiInsightsApprovedListingsSectionTitle(): string {
  return "Approved Sell It Off listings (context)";
}

export function getAiInsightsApprovedListingsSectionIntro(): string {
  return "Browse approved marketplace listings in this category. Price suggestions use the platform data source configured in Admin → Settings — these tables are for review context when using market reference.";
}

export function getSimilarApprovedListingsTitle(): string {
  return "SIO listing comps";
}

export function getSimilarApprovedListingsSubtitle(): string {
  return "Eligible Sell It Off listings in this category — approved, completed sales, and pricing-eligible archived rows.";
}

export function getSimilarApprovedListingsTableHeading(count: number): string {
  return `SIO listing comps (${count})`;
}

export function getAdminModalPricingSourceToggleLabel(): string {
  return "Benchmark data for this review";
}

export function getAdminModalPricingSourceToggleHint(): string {
  return "Session-only — does not change Admin → Settings. Affects band, comps, and reviewer analysis below.";
}

export function getAdminPricingExploreLabel(): string {
  return "Explore cohort filters";
}

export function getAdminPricingExploreHint(): string {
  return "Try alternate typed filters for this review only. Does not change the listing.";
}

export function getAdminPricingExploreMixedLabel(): string {
  return "Mixed";
}

export function getAdminPricingExploreResetLabel(): string {
  return "Reset to listing";
}

export function getAdminPricingExploringBanner(): string {
  return "Exploring — not the listing’s saved attrs. Band chips and comps use this session filter.";
}

export function getAdminSellerBandPreviewTitle(): string {
  return "What the seller sees";
}

export function getAdminSellerBandPreviewSubtitle(): string {
  return "Same market band and chip options as /sell for the selected benchmark source.";
}

export function getAdminReviewerAiInsightTitle(
  aiProvider?: PricingAiProviderUi,
): string {
  return usesAiAssistedPricing(aiProvider)
    ? "Reviewer AI analysis"
    : "Reviewer price analysis";
}

export function getAdminReviewerAiInsightSubtitle(
  dataSource: PricingDataSourceInput,
  aiProvider?: PricingAiProviderUi,
): string {
  const statsSource = getPricingStatsSourcePhrase(dataSource);
  if (usesAiAssistedPricing(aiProvider)) {
    return `AI-assisted insight from ${statsSource} — optional context for your decision.`;
  }
  return `Market-data insight from ${statsSource} — optional context for your decision.`;
}

export function getAdminReviewerInsightDisabledTitle(): string {
  return "Reviewer price analysis";
}

export function getAdminReviewerInsightDisabledSubtitle(): string {
  return "Price suggestions (disabled)";
}

export function getAdminReviewerInsightDisabledBody(): string {
  return "Price analysis is currently turned off. Update pricing insight settings to enable this feature.";
}

export function getAdminReviewerInsightLoadingLabel(): string {
  return "Preparing price analysis…";
}

export function getAdminReviewerInsightCtaLabel(
  aiProvider?: PricingAiProviderUi,
): string {
  return usesAiAssistedPricing(aiProvider)
    ? "Get reviewer AI insight"
    : "Get reviewer price insight";
}

/** Admin → Settings: admin Price Analysis feature toggle. */
export function getAdminPricingInsightFeatureBlurb(
  aiProvider?: PricingAiProviderUi,
): string {
  if (usesAiAssistedPricing(aiProvider)) {
    return "Enable price analysis for administrators (AI-assisted when Genkit or OpenAI is selected below).";
  }
  return "Enable market-data price analysis for administrators.";
}

/** Admin → Settings: user Price Assistant feature toggle. */
export function getUserPricingInsightFeatureBlurb(): string {
  return "Enable market-based price suggestions for sellers when listing items.";
}

/** Admin → Pricing Insights page header. */
export function getPricingInsightsPageSubtitle(
  aiProvider?: PricingAiProviderUi,
): string {
  if (usesAiAssistedPricing(aiProvider)) {
    return "Analyze pricing data, run weekly battle review, and get AI-assisted price suggestions.";
  }
  return "Analyze pricing data, run weekly battle review, and get market-based price suggestions.";
}

/** Insights → AI Pricing Assistant empty state. */
export function getAiInsightsEmptyPrompt(
  aiProvider?: PricingAiProviderUi,
): string {
  if (usesAiAssistedPricing(aiProvider)) {
    return "Enter an item title and select a category to get an AI-assisted price suggestion.";
  }
  return "Enter an item title and select a category to get a market-based price suggestion.";
}

export function getMarketReferenceCompsTitle(): string {
  return "Market reference comps";
}

export function getMarketReferenceCompsSubtitle(): string {
  return "Synced external market rows from pricing_reference_listings (not live Google Sheets).";
}

export function getMarketReferenceCompsTableHeading(count: number): string {
  return `Market reference comps (${count})`;
}

export function getMarketReferenceCompsEmptyMessage(): string {
  return "No synced market reference comps for this category. Run a reference sync in Admin → Settings or check taxonomy alignment.";
}

export function getAdminProductCompsPanelTitle(): string {
  return "Market comps";
}

export function getAdminProductCompsPanelSubtitle(): string {
  return "Comparable SIO listings and synced external market rows for this category.";
}

export function getAdminProductCompsSioTabLabel(): string {
  return "SIO listings";
}

export function getAdminProductCompsReferenceTabLabel(): string {
  return "Market reference";
}

export function getAdminPriceSuitabilityColumnLabel(): string {
  return "Price vs band";
}

export function getAdminPriceSuitabilityInBand(): string {
  return "In range";
}

export function getAdminPriceSuitabilityAboveBand(): string {
  return "Above band";
}

export function getAdminPriceSuitabilityBelowBand(): string {
  return "Below band";
}

export function getAdminPriceSuitabilityNoData(): string {
  return "No band";
}

export function getAdminPriceSuitabilityLoading(): string {
  return "…";
}

export function getDataSourceBadgeLabel(
  dataSource: PricingDataSourceInput,
): string {
  return getPricingDataSourceBadgeLabel(dataSource);
}

export function getAiProviderBadgeLabel(
  aiProvider: "genkit" | "openai" | "none" | undefined,
): string | undefined {
  if (!aiProvider || aiProvider === "none") return undefined;
  if (aiProvider === "genkit") return "Genkit AI";
  return "OpenAI";
}

/** Shown above the price field when band enforcement is active. */
export function getSellerBandEnforcementHeading(): string {
  return "Typical market price range";
}

const BAND_INTRO_GENERIC =
  "Similar items on Sell It Off often sell in this range. You may price lower for a faster sale.";

/** Pluralize the last word for “market data for …” phrasing (e.g. Smart TV → Smart TVs). */
function pluralizeLastWordForMarketLabel(phrase: string): string {
  const parts = phrase.trim().split(/\s+/);
  if (parts.length === 0) return phrase;

  const last = parts[parts.length - 1];
  const lower = last.toLowerCase();

  const irregular: Record<string, string> = {
    tv: "TVs",
    pc: "PCs",
  };

  let pluralLast: string;
  if (irregular[lower]) {
    pluralLast = irregular[lower];
  } else if (/[^aeiou]y$/i.test(last)) {
    pluralLast = `${last.slice(0, -1)}ies`;
  } else if (/s$/i.test(last)) {
    pluralLast = last;
  } else {
    pluralLast = `${last}s`;
  }

  parts[parts.length - 1] = pluralLast;
  return parts.join(" ");
}

/**
 * Human-readable item label for band intro copy (composed title from sell flow).
 * Returns null when title is too short to read naturally in a sentence.
 */
export function formatSellerBandMarketLabel(
  itemTitle: string | undefined,
): string | null {
  const trimmed = itemTitle?.trim();
  if (!trimmed || trimmed.length < 3) {
    return null;
  }
  return pluralizeLastWordForMarketLabel(trimmed);
}

export function getSellerBandEnforcementIntro(itemTitle?: string): string {
  const label = formatSellerBandMarketLabel(itemTitle);
  if (!label) {
    return BAND_INTRO_GENERIC;
  }
  return `Similar ${label} on Sell It Off often sell in this range. You may price lower for a faster sale.`;
}

export function getSellerBandThinDataMessage(): string {
  return "We have limited market data for this item. You can still list your price — our team may review it and contact you if an adjustment is needed.";
}

export function getSellerBandUnavailableMessage(): string {
  return "Market price range is unavailable right now. You can still list using the platform minimum and maximum. Check back later or contact support if this persists.";
}

/**
 * Opaque above-ceiling copy — never names the hard max (avoids training sellers to fish for it).
 * `@deprecated` max arg kept for call-site compatibility; ignored.
 */
export function getSellerBandAboveRangeMessage(_max?: number): string {
  return "That price is higher than similar items usually sell for.";
}

/** Primary recovery CTA when price is above the enforced ceiling. */
export function getSellerBandUseRecommendedLabel(
  recommendedPrice: number,
): string {
  return `Use recommended · ₦${recommendedPrice.toLocaleString("en-NG")}`;
}

export function getSellerBandAboveRangeSecondaryHint(): string {
  return "Try a lower price, or pick one of the options above.";
}

export function getSellerBandBelowRangeMessage(): string {
  return "Below the typical range — you can still list at this price for a faster sale.";
}

export function getSellerBandInRangeMessage(): string {
  return "Within the typical range for similar items.";
}

/** @deprecated Use getSellerBandAboveRangeMessage for ceiling-only enforcement. */
export function getSellerBandOutOfRangeMessage(
  _min?: number,
  _max?: number,
): string {
  return getSellerBandAboveRangeMessage();
}

export function getSellerBandTypicalRangeLabel(): string {
  return "Typical range";
}

export function getSellerBandChipsEnforcementHeading(): string {
  return "Price your item for how fast you want to sell";
}

export function getSellerBandChipsEnforcementIntro(itemTitle?: string): string {
  const label = formatSellerBandMarketLabel(itemTitle);
  if (!label) {
    return "Pick a price below based on market data. Lower prices may attract more buyers.";
  }
  return `Pick a price for similar ${label} based on market data. Lower options may attract more buyers.`;
}

export function getSellerBandRecommendationChipsHeading(): string {
  return "Choose how quickly you want to sell";
}

export function getSellerBandChipTierLabel(tier: BandPriceChipTier): string {
  switch (tier) {
    case "standard":
      return "Standard";
    case "sell_sooner":
      return "Sell sooner";
    case "sell_fastest":
      return "Sell fastest";
  }
}

export function getSellerBandChipTierSubtitle(tier: BandPriceChipTier): string {
  switch (tier) {
    case "standard":
      return "Balanced — matches usual market level";
    case "sell_sooner":
      return "Low end of typical market — may attract more interest";
    case "sell_fastest":
      return "Aggressive price — priced for visibility";
  }
}

export function getSellerBandChipRecommendedBadge(): string {
  return "Recommended";
}

export function getSellerBandChipsFooterHint(): string {
  return "You can enter any price below these suggestions for an even quicker sale.";
}

/** @deprecated Phase 4.7 — use getSellerBandChipTierLabel */
export function getSellerBandMidChipLabel(): string {
  return getSellerBandChipTierLabel("standard");
}

/** @deprecated Phase 4.7 — use getSellerBandChipTierLabel */
export function getSellerBandQuickSaleChipLabel(): string {
  return getSellerBandChipTierLabel("sell_fastest");
}

/** @deprecated Phase 4.7 — use getSellerBandChipTierLabel */
export function getSellerBandLowestTypicalChipLabel(): string {
  return getSellerBandChipTierLabel("sell_sooner");
}

/** @deprecated Phase 4.7 — use getSellerBandChipsFooterHint */
export function getSellerBandQuickSaleChipHint(): string {
  return getSellerBandChipsFooterHint();
}

/** @deprecated Phase 4.7 — use getSellerBandChipsFooterHint */
export function getSellerBandFasterSaleWithinRangeHint(): string {
  return getSellerBandChipsFooterHint();
}

export function getSellerBandPolicyHint(
  policy: SellPriceBandPolicy | undefined,
): string | null {
  if (policy === "warn_allow") {
    return getSellerBandThinDataMessage();
  }
  if (policy === "unavailable") {
    return getSellerBandUnavailableMessage();
  }
  return null;
}

/** Phase 5 — thin-data hint from cohort tier; never exposes comp counts. */
export function resolveSellerBandPolicyHint(options: {
  bandPolicy?: SellPriceBandPolicy;
  cohortTier?: CohortFallbackTierDraft;
  enforcementEligible?: boolean;
}): string | null {
  if (options.enforcementEligible) {
    return null;
  }
  if (options.cohortTier && options.cohortTier !== "none") {
    return getSellerBandThinCohortMessage(options.cohortTier);
  }
  return getSellerBandPolicyHint(options.bandPolicy);
}

/** Phase 5 — generic lead-in when band is advisory; no segment pool size. */
export function getSellerBandTypicalRangeLabelForBand(
  band: { enforcementEligible?: boolean } | null | undefined,
): string {
  if (band && !band.enforcementEligible) {
    return getSellerBandTypicalRangeLeadIn();
  }
  return getSellerBandTypicalRangeLabel();
}

// ---------------------------------------------------------------------------
// Phase 0 draft — thin cohort fallback copy keys (wire in Phases 4–5)
// See: docs/project-management/2-in-progress (2-max)/pricing-thin-cohort-fallback/
// ---------------------------------------------------------------------------

/** Mirrors `CohortFallbackTier` — prefer importing from `@/lib/pricingTitleCohort`. */
export type CohortFallbackTierDraft =
  import("@/lib/pricingTitleCohort").CohortFallbackTier;

export function getAdminBandInformationalOnlySuffix(): string {
  return "informational — not enforced";
}

/** Admin band subtitle when stats used an exact or fuzzy cohort (Phase 4). */
export function getAdminBandCohortSubtitle(options: {
  tier: "exact" | "fuzzy";
  exactCount: number;
  signalSummary: string;
  fuzzyToleranceLabel?: string;
}): string {
  const { tier, exactCount, signalSummary, fuzzyToleranceLabel } = options;
  if (tier === "fuzzy" && fuzzyToleranceLabel) {
    return `Based on ${exactCount} exact ${signalSummary} listings plus nearby capacities (${fuzzyToleranceLabel}) — limited data.`;
  }
  return `Based on ${exactCount} exact ${signalSummary} listings (limited data).`;
}

/** Admin band subtitle when cohort widened to full subcategory segment (Phase 4 / soft-widen D7). */
export function getAdminBandSegmentFallbackSubtitle(
  signalSummary: string,
  segmentCount: number,
  subcategoryLabel: string,
): string {
  return `Not enough ${signalSummary} matches — range uses all ${subcategoryLabel} listings in this subcategory (${segmentCount} listings).`;
}

/** Admin band subtitle when stats use 1–4 exact cohort rows (soft-widen thin_exact). */
export function getAdminBandThinExactSubtitle(
  signalSummary: string,
  exactCount: number,
): string {
  return `Based on ${exactCount} exact ${signalSummary} listings (limited data) — advisory, not enforced.`;
}

/** Admin comps table — exact cohort section heading (Phase 4). */
export function getCompsExactCohortHeading(
  signalSummary: string,
  count: number,
): string {
  return `Showing ${signalSummary} matches (${count} rows)`;
}

/** Admin comps table — expandable nearby capacities label (Phase 4, D5). */
export function getCompsNearbyCapacitiesLabel(
  toleranceLabel: string,
  count: number,
): string {
  return `Nearby: ${toleranceLabel} — ${count} listings`;
}

/**
 * Seller outcome copy by cohort tier (Phase 5, D6).
 * Outcome-only — no comp counts or cohort mechanics per copy audience split.
 */
export function getSellerBandThinCohortMessage(
  tier: CohortFallbackTierDraft,
): string | null {
  switch (tier) {
    case "exact":
    case "fuzzy":
    case "thin_exact":
    case "segment":
      return getSellerBandThinDataMessage();
    case "insufficient":
      return getSellerBandThinDataMessage();
    case "none":
      return null;
    default: {
      const _exhaustive: never = tier;
      return _exhaustive;
    }
  }
}

/** Seller typical-range lead-in — always generic, never segment pool size (Phase 5). */
export function getSellerBandTypicalRangeLeadIn(): string {
  return "Typical range for similar items:";
}

/** Tooltip when admin suitability shows no_band — insufficient market data. */
export function getAdminPriceSuitabilityNoBandTooltip(): string {
  return "Not enough market data for an enforced price band.";
}

/** Tooltip when band exists but cohort quality is advisory (segment / thin cohort). */
export function getAdminPriceSuitabilityAdvisoryBandTooltip(): string {
  return "Band is informational only — cohort data is too limited for enforcement.";
}

export type AdminBandContextDisplay = {
  subtitle: string | null;
  showInformationalSuffix: boolean;
  provenanceLine: string | null;
};

/** Admin review modal — cohort tier, counts, and combined branch context (Phase 4). */
export function buildAdminBandContextDisplay(options: {
  cohortTier?: CohortFallbackTierDraft;
  exactCount?: number;
  cohortCompCount?: number;
  segmentCompCount?: number;
  cohortMatchMode?: TitleCohortMatchMode;
  signalSummary?: string | null;
  fuzzyToleranceLabel?: string | null;
  categoryLabel?: string;
  /** Prefer subcategory display name for segment fallback (D7). */
  subcategoryLabel?: string;
  sampleCount?: number;
  combinedProvenance?: CombinedPricingProvenance;
  dataSource?: PricingDataSource;
  enforcementEligible?: boolean;
}): AdminBandContextDisplay {
  const {
    cohortTier,
    exactCount,
    cohortCompCount,
    segmentCompCount,
    cohortMatchMode,
    signalSummary,
    fuzzyToleranceLabel,
    categoryLabel,
    subcategoryLabel,
    sampleCount,
    combinedProvenance,
    dataSource,
    enforcementEligible,
  } = options;

  const provenanceLine =
    dataSource === "combined" && combinedProvenance
      ? getCombinedCohortProvenanceLabel(combinedProvenance)
      : null;

  if (
    dataSource === "combined" &&
    combinedProvenance?.primaryBranch &&
    signalSummary &&
    cohortCompCount != null &&
    cohortCompCount > 0
  ) {
    const branchLabel =
      combinedProvenance.primaryBranch === "market_reference"
        ? "market reference"
        : "SIO";
    const matchDetail =
      cohortMatchMode === "fuzzy"
        ? `${signalSummary} nearby`
        : `${signalSummary} exact`;
    return {
      subtitle: `Based on ${cohortCompCount} ${branchLabel} listings (${matchDetail})`,
      showInformationalSuffix: !enforcementEligible,
      provenanceLine,
    };
  }

  switch (cohortTier) {
    case "exact": {
      if (signalSummary && cohortCompCount != null && cohortCompCount >= 5) {
        return {
          subtitle: `Based on ${cohortCompCount} exact ${signalSummary} listings`,
          showInformationalSuffix: false,
          provenanceLine,
        };
      }
      if (signalSummary && exactCount != null) {
        return {
          subtitle: getAdminBandCohortSubtitle({
            tier: "exact",
            exactCount,
            signalSummary,
          }),
          showInformationalSuffix: false,
          provenanceLine,
        };
      }
      break;
    }
    case "thin_exact": {
      if (signalSummary && exactCount != null && exactCount > 0) {
        return {
          subtitle: getAdminBandThinExactSubtitle(signalSummary, exactCount),
          showInformationalSuffix: true,
          provenanceLine,
        };
      }
      break;
    }
    case "fuzzy": {
      if (signalSummary && exactCount != null) {
        return {
          subtitle: getAdminBandCohortSubtitle({
            tier: "fuzzy",
            exactCount,
            signalSummary,
            fuzzyToleranceLabel: fuzzyToleranceLabel ?? undefined,
          }),
          showInformationalSuffix: false,
          provenanceLine,
        };
      }
      break;
    }
    case "segment": {
      const poolLabel = subcategoryLabel ?? categoryLabel;
      if (signalSummary && poolLabel) {
        const count = segmentCompCount ?? sampleCount ?? 0;
        return {
          subtitle: getAdminBandSegmentFallbackSubtitle(
            signalSummary,
            count,
            poolLabel,
          ),
          showInformationalSuffix: true,
          provenanceLine,
        };
      }
      break;
    }
    case "insufficient":
      return {
        subtitle:
          "Not enough cohort or segment listings to build a reliable band.",
        showInformationalSuffix: true,
        provenanceLine,
      };
    case "none":
    default:
      break;
  }

  if ((sampleCount ?? 0) > 0) {
    return {
      subtitle: `Based on ${sampleCount} listings in this category (no title cohort).`,
      showInformationalSuffix: !enforcementEligible,
      provenanceLine,
    };
  }

  return { subtitle: null, showInformationalSuffix: false, provenanceLine };
}

export function resolveAdminPriceSuitabilityNoBandTooltip(
  status: AdminPriceSuitabilityStatus | undefined,
  cohortTier?: CohortFallbackTierDraft,
): string | undefined {
  if (status !== "no_band" && status !== "skipped") {
    return undefined;
  }
  if (cohortTier === "segment" || cohortTier === "thin_exact") {
    return getAdminPriceSuitabilityAdvisoryBandTooltip();
  }
  if (
    cohortTier === "exact" ||
    cohortTier === "fuzzy" ||
    cohortTier === "insufficient"
  ) {
    return getAdminPriceSuitabilityAdvisoryBandTooltip();
  }
  return getAdminPriceSuitabilityNoBandTooltip();
}
